import { useState, useCallback, useMemo } from 'react';
import { getStatusLabel, getGameMetadata, isTerminal as isGameTerminal } from '@swtorbit/shared-socket-events';
import { getEnabledPlayers } from '@swtorbit/shared-react-hooks';
import { useGameContext } from '../contexts/GameContext';
import { GameSelectorDropdown } from './GameSelectorDropdown';
import { LifecycleModal } from './LifecycleModal';

interface GamePanelProps {
  onViewAllGames: () => void;
  onCreateGame: () => void;
}

export function GamePanel({ onViewAllGames, onCreateGame }: GamePanelProps) {
  const { selectedGame } = useGameContext();
  const [showLifecycleModal, setShowLifecycleModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const statusInfo = useMemo(
    () => (selectedGame ? getStatusLabel(selectedGame.status) : null),
    [selectedGame],
  );

  const gameTitle = useMemo(
    () => (selectedGame ? getGameMetadata(selectedGame.gameType).title : null),
    [selectedGame],
  );

  const enabledPlayers = useMemo(
    () => getEnabledPlayers(selectedGame),
    [selectedGame?.players],
  );

  const isTerminal =
    selectedGame ? isGameTerminal(selectedGame.gameType, selectedGame.status) : false;

  const handleCopyRoomCode = useCallback(() => {
    if (!selectedGame) return;
    navigator.clipboard.writeText(selectedGame.roomCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [selectedGame]);

  return (
    <div className="game-panel d-flex align-items-stretch gap-xs justify-content-center">
      {selectedGame && statusInfo ? (
        <>
          {/* Game identity — type, optional name, player count */}
          <div className="d-flex flex-column justify-content-center me-2xs">
            <span className="fs-sm fw-bold text-white">{gameTitle}</span>
            <span className="fs-xs text-white">
              {selectedGame.name ? `${selectedGame.name} · ` : ''}
              {enabledPlayers.length} player{enabledPlayers.length !== 1 ? 's' : ''}
            </span>
            {selectedGame.archived && (
              <span
                className="fs-xs text-amber fw-bold"
                title="A finished game, opened from the archive. It can be viewed but not changed."
              >
                Archived · view only
              </span>
            )}
          </div>

          {/* Room code box — CSS overlay reveals COPY on hover, COPIED on click */}
          <button
            className={`game-panel__room-code-badge d-flex align-items-center gap-xs${copied ? ' is-copied' : ''}`}
            onClick={handleCopyRoomCode}
            title={
              selectedGame.roomCodeReleased
                ? 'This game released its room code; it may now belong to another game. Click to copy.'
                : 'Click to copy room code'
            }
          >
            <span className="game-panel__overlay" aria-hidden="true">
              {copied ? '✓ COPIED!' : 'COPY'}
            </span>
            <span className="d-flex flex-column">
              <span className="fs-xs text-uppercase text-cyan-light fw-bold">
                {selectedGame.roomCodeReleased ? 'Room Code (released)' : 'Room Code'}
              </span>
              <span className="game-panel__room-code-value">{selectedGame.roomCode}</span>
            </span>
          </button>

          {/* Lifecycle status box — click to manage lifecycle */}
          <button
            className={`game-panel__status-badge d-flex align-items-center gap-xs${!isTerminal ? ' is-clickable' : ''}`}
            onClick={!isTerminal ? () => setShowLifecycleModal(true) : undefined}
            title={!isTerminal ? 'Manage lifecycle' : undefined}
          >
            {!isTerminal && (
              <span className="game-panel__overlay" aria-hidden="true">CHANGE</span>
            )}
            <span className="d-flex flex-column">
              <span className="fs-xs text-uppercase text-cyan-light fw-bold">
                Status
              </span>
              <span
                className="game-panel__room-code-value"
                title={selectedGame.endReason === 'inactive' ? 'Abandoned automatically after 30 days without activity' : undefined}
              >
                {statusInfo.label}
                {selectedGame.endReason === 'inactive' ? ' (inactive)' : ''}
              </span>
            </span>
          </button>

          <LifecycleModal
            isOpen={showLifecycleModal}
            onClose={() => setShowLifecycleModal(false)}
          />
        </>
      ) : (
        <span className="d-flex align-items-center text-white fs-sm fw-bold">No game selected</span>
      )}

      {/* Game selector dropdown */}
      <GameSelectorDropdown
        onViewAllGames={onViewAllGames}
        onCreateGame={onCreateGame}
      />
    </div>
  );
}
